import React from 'react';
import { styled, Theme } from '@mui/material/styles';
import { Box, Divider, ListItemText, useTheme, Skeleton, ListItemButton } from '@mui/material';

import clsx from 'clsx';
import { useCalories } from 'src/hooks/useCalories';
import { addOpacity, showCalories } from 'lib/helpers';
import { isDefined, isNumber } from 'lib/typeInference';
import { useCurrencyString } from 'lib/useCurrencyString';
import {
    getModifierOptionsForProduct,
    getProductData,
    IEnrichedMenuWithModifierMaps,
    IEnrichedProduct,
    IProductTagValue,
    ProductGroup,
    getNestedModifierProductsForProduct,
    getDefaultSelectedModifierProductsPrice
} from './model/Menu';
import { useTranslation } from 'react-i18next';
import { SwipeableListItem } from 'ui/SwipeableListItem';
import DeleteIcon from '@mui/icons-material/Delete';
import { EGAEventName, useGAHelpers } from 'lib/useGAHelpers';

const PREFIX = 'MenuProduct';

const classes = {
    listItem: `${PREFIX}listItem`,
    box: `${PREFIX}box`,
    description: `${PREFIX}description`,
    imageWrapper: `${PREFIX}imageWrapper`,
    content: `${PREFIX}content`,
    fontWeightBold: `${PREFIX}fontWeightBold`,
    image: `${PREFIX}image`,
    imageLoader: `${PREFIX}imageLoader`,
    selectedItem: `${PREFIX}selectedItem`,
    imageHidden: `${PREFIX}imageHidden`,
    tagImageContainer: `${PREFIX}tagImageContainer`,
    tagImage: `${PREFIX}tagImage`,
    itemDescription: `${PREFIX}itemDescription`,
    deleteIcon: `${PREFIX}deleteIcon`
};

const getListItemStyles = (
    theme: Theme,
    listingBackgroundImageUrl?: string,
    listingTextColour?: string,
    listingBackgroundColour?: string
) => {
    const isBackgroundImage = isDefined(listingBackgroundImageUrl);
    const defaultStyles = {
        padding: theme.spacing(1, 1.5),
        color: listingTextColour ?? theme.palette.text.primary
    };

    if (isBackgroundImage) {
        return {
            ...defaultStyles,
            position: 'relative',
            '&:before': {
                content: "' '",
                display: 'block',
                position: 'absolute',
                left: 0,
                top: 0,
                width: '100%',
                height: '100%',
                opacity: 1,
                backgroundImage: `url(${listingBackgroundImageUrl})`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: '50% 0',
                backgroundSize: '100% 100%'
            }
        };
    }

    if (isDefined(listingBackgroundColour)) {
        return {
            ...defaultStyles,
            background: listingBackgroundColour,
            '&:hover': {
                background: addOpacity(listingBackgroundColour, 0.95)
            }
        };
    }

    return defaultStyles;
};

const Root = styled('div', {
    shouldForwardProp: (prop: string) =>
        !['listingBackgroundColour', 'listingBackgroundImageUrl', 'listingTextColour'].includes(prop)
})<ProductStyles>(({ theme, listingBackgroundColour, listingBackgroundImageUrl, listingTextColour }) => ({
    [`& .${classes.listItem}`]: getListItemStyles(
        theme,
        listingBackgroundImageUrl,
        listingTextColour,
        listingBackgroundColour
    ),

    [`& .${classes.box}`]: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flex: 1,
        zIndex: 1
    },

    [`& .${classes.description}`]: {
        '& .MuiListItemText-secondary': {
            color: listingTextColour ?? theme.palette.text.primary,
            opacity: 0.6,
            overflow: 'hidden',
            position: 'relative',
            maxHeight: '3em',
            paddingRight: '1em'
        }
    },
    [`& .${classes.imageWrapper}`]: {
        width: '80px',
        height: '80px',
        marginLeft: theme.spacing(2),
        position: 'relative',
        flexShrink: 0,
        overflow: 'hidden',
        borderRadius: theme.shape.borderRadius
    },

    [`& .${classes.content}`]: {
        display: 'flex',
        alignItems: 'space-between',
        height: '100%',
        flexDirection: 'column'
    },

    [`& .${classes.fontWeightBold}`]: {
        fontWeight: theme.typography.fontWeightBold
    },

    [`& .${classes.image}`]: {
        width: '100%',
        height: '100%',
        objectFit: 'cover'
    },

    [`& .${classes.imageLoader}`]: {
        borderRadius: theme.shape.borderRadius,
        marginLeft: theme.spacing(2),
        flexShrink: 0
    },

    [`& .${classes.selectedItem}`]: {
        borderLeft: `${theme.spacing(0.75)} solid ${listingTextColour ?? theme.palette.text.primary}`,
        paddingLeft: theme.spacing(1)
    },

    [`& .${classes.imageHidden}`]: {
        visibility: 'hidden',
        width: 0,
        height: 0,
        padding: 0,
        margin: 0
    },

    [`& .${classes.tagImageContainer}`]: {
        height: 22,
        width: 22
    },

    [`& .${classes.tagImage}`]: {
        width: '100%',
        height: '100%',
        objectFit: 'contain'
    },

    [`& .${classes.itemDescription}`]: {
        flex: 'unset'
    },

    [`& .${classes.deleteIcon}`]: {
        fontSize: '26px',
        color: theme.palette.error.contrastText
    }
}));

interface MenuProductProps {
    item: IEnrichedProduct | ProductGroup;
    quantity: number;
    onClick?: (product: IEnrichedProduct | ProductGroup) => void;
    onRemove?: (product: IEnrichedProduct | ProductGroup) => void;
    menu?: IEnrichedMenuWithModifierMaps;
    isDesktop?: boolean;
    enableSwipe?: boolean;
}

interface ProductStyles {
    listingBackgroundColour?: string;
    listingBackgroundImageUrl?: string;
    listingTextColour?: string;
}

export const MenuProduct: React.FC<React.PropsWithChildren<MenuProductProps>> = ({
    item,
    onClick,
    quantity,
    menu,
    isDesktop,
    onRemove,
    enableSwipe = true
}) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { getCaloriesString, getProductCalories } = useCalories();
    const getCurrencyString = useCurrencyString();
    const { logUserEvent } = useGAHelpers();

    const [imageLoaded, setImageLoaded] = React.useState(false);
    const handleClick = React.useCallback(() => {
        if (onClick) {
            onClick(item);
        }
    }, [onClick, item]);
    const {
        description,
        title,
        image,
        listingImage,
        price,
        available,
        modifiers,
        tags,
        listingBackgroundColour,
        listingBackgroundImageUrl,
        listingTextColour,
        noDefault
    } = React.useMemo(() => getProductData(item, quantity), [item, quantity]);
    const listingStyles = React.useMemo(
        () => (isDesktop ? {} : { listingBackgroundColour, listingBackgroundImageUrl, listingTextColour }),
        [isDesktop, listingBackgroundColour, listingBackgroundImageUrl, listingTextColour]
    );
    const productModifiers = React.useMemo(
        () => (menu ? getNestedModifierProductsForProduct(menu, modifiers || []) : []),
        [menu, modifiers]
    );
    const optionModifiers = React.useMemo(
        () => (menu ? getModifierOptionsForProduct(menu, item.id, modifiers) : []),
        [item.id, menu, modifiers]
    );
    const priceWithModifiers = React.useMemo(() => {
        const productModifiersPrice = getDefaultSelectedModifierProductsPrice(productModifiers);
        const optionModifiersPrice = optionModifiers.reduce((acc, modifier) => {
            if (modifier.options.length > 0) {
                const selectedModifiers = modifier.options.filter(modifierOption => modifierOption.selected);
                if (selectedModifiers.length) {
                    const localPrice = selectedModifiers
                        .slice(0, modifier.max)
                        .reduce((modifierPrice, modifierOption) => modifierPrice + modifierOption.price, 0);
                    return acc + localPrice;
                }
                if (modifier.bundle && isNumber(modifier.min) && modifier.min > 0) {
                    const minPriceOption = modifier.options.reduce((minPrice, modifierOption) => {
                        if (minPrice <= modifierOption.price) {
                            return minPrice;
                        }
                        return modifierOption.price;
                    }, modifier.options[0].price);
                    return acc + minPriceOption * modifier.min;
                }
            }
            return acc;
        }, 0);
        return (price || 0) + productModifiersPrice + optionModifiersPrice;
    }, [productModifiers, optionModifiers, price]);
    const handleImageLoaded = React.useCallback(() => {
        setImageLoaded(true);
    }, []);
    const calories = React.useMemo(() => getProductCalories(item), [getProductCalories, item]);
    const itemDescription = React.useMemo(() => {
        if (noDefault) {
            return `${t('PREORDER_VARIOUS_PRICE')} ${
                showCalories(calories) ? ` | ${getCaloriesString(calories.value)}` : ''
            }`;
        }
        if (priceWithModifiers) {
            return `${getCurrencyString(priceWithModifiers)} ${
                showCalories(calories) ? ` | ${getCaloriesString(calories.value)}` : ''
            }`;
        }

        return calories.isSet ? getCaloriesString(calories.value) : '';
    }, [calories, getCaloriesString, getCurrencyString, noDefault, priceWithModifiers, t]);

    const renderTag = React.useCallback(
        (tag: IProductTagValue) =>
            !!tag.imageUrl && (
                <Box marginLeft={0.75} className={classes.tagImageContainer} key={tag.title}>
                    <img className={classes.tagImage} src={tag.imageUrl} alt="" />
                </Box>
            ),
        []
    );

    const handleSwipeLeft = React.useCallback(() => {
        if (quantity !== 0) {
            onRemove?.(item);
            logUserEvent(EGAEventName.SwipeToRemoveItem);
        }
    }, [quantity, onRemove, item, logUserEvent]);

    return (
        <Root {...listingStyles}>
            <Box paddingX={1.5}>
                <Divider />
            </Box>
            <SwipeableListItem
                enableRightToLeft={enableSwipe && quantity !== 0}
                onSwipeLeft={handleSwipeLeft}
                rightToLeftBg={theme.palette.error.main}
                rightToLeftIcon={<DeleteIcon className={classes.deleteIcon} />}
            >
                <ListItemButton
                    disableGutters
                    disabled={!available}
                    onClick={handleClick}
                    className={clsx(classes.listItem, { [classes.selectedItem]: quantity !== 0 })}
                >
                    <div className={classes.box}>
                        <div className={classes.content}>
                            <ListItemText
                                primary={title}
                                slotProps={{
                                    primary: {
                                        className: classes.fontWeightBold,
                                        variant: 'h6'
                                    }
                                }}
                            />
                            {!!description && (
                                <ListItemText secondary={description} className={classes.description} />
                            )}
                            <Box display="flex" alignItems="center">
                                {itemDescription.length > 0 && (
                                    <ListItemText
                                        className={classes.itemDescription}
                                        secondary={itemDescription}
                                        slotProps={{
                                            secondary: {
                                                color: 'inherit'
                                            }
                                        }}
                                    />
                                )}

                                {!!tags && !!tags.length && (
                                    <Box marginLeft={0} display="flex">
                                        {tags.map(renderTag)}
                                    </Box>
                                )}
                            </Box>
                        </div>
                        {(!!image || !!listingImage) && (
                            <>
                                <Box
                                    className={clsx(classes.imageWrapper, {
                                        [classes.imageHidden]: !imageLoaded
                                    })}
                                >
                                    <img
                                        className={classes.image}
                                        onLoad={handleImageLoaded}
                                        src={listingImage || image}
                                    />
                                </Box>
                                {!imageLoaded && (
                                    <Skeleton className={classes.imageWrapper} variant="rectangular" />
                                )}
                            </>
                        )}
                    </div>
                </ListItemButton>
            </SwipeableListItem>
        </Root>
    );
};
